import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { useTranslation } from 'react-i18next';
import './style.css';
import i18n from 'i18next';
import NotificationBell from 'components/NotificationBell';
import { jsPDF } from 'jspdf';
import { buildGoodDeliveryQuery } from './goodDeliveryConfig';

const OrdersAdmin = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [searchID, setSearchID] = useState('');    
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [deliveryLoadingId, setDeliveryLoadingId] = useState(null);
    const currentLang = i18n.language;

    // --- Edit Modal States ---
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [editForm, setEditForm] = useState({
      Name: '',
      Phone: '',
      Address: '',
      City: '',
      notes: '',
      TotalAmount: 0,
      items: []
    });

    useEffect(() => {
      const adminFlag = localStorage.getItem('isAdmin') === '1';
      setIsAdmin(adminFlag);
      if (adminFlag) {
        fetchAllOrders(1);
      }
    }, []);

    useEffect(() => {
        if (isAdmin) {
          fetchAllOrders(currentPage);  
        }
    }, [isAdmin, currentPage]);

    const fetchAllOrders = async (page) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`https://jolyaparis.cloud/api/admin/orders?page=${page}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
              const data = Array.isArray(response.data.data) ? response.data.data : [];
              setOrders(data);  
              setTotalPages(response.data.last_page ?? 1);  
            } else {
                console.error('Failed to fetch orders:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };
console.log(orders);
    const getOrderUniqueId = (order) => String(order?.OrderID ?? order?.idsOrder ?? '');

    useEffect(() => {
      setSelectedOrderIds((prev) => prev.filter((id) => orders.some((order) => getOrderUniqueId(order) === id)));
    }, [orders]);

    const handleStatusChange = async (orderID, newStatus, paymentStatus) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.put(
                `https://jolyaparis.cloud/api/admin/orders/${orderID}/tracking`, 
                { CurrentStatus: newStatus },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.status === 200) {
              fetchAllOrders(currentPage);  
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handlePageChange = (selectedPage) => {
      if (selectedPage < 1 || selectedPage > totalPages) return;
      setCurrentPage(selectedPage);
    };

    if (!isAdmin) {
        return <p className="text-danger text-center">Unauthorized: You do not have admin access.</p>;
    }

    const filteredOrders = orders.filter((order) => {
      const orderStatus = order.Status ?? order.status;
      const matchStatus = statusFilter ? orderStatus === statusFilter : true;

      const matchDate = dateFilter
        ? (() => {
            const orderDate = new Date(order.created_at);
            const today = new Date();
            const pastDate = new Date();
            pastDate.setDate(today.getDate() - parseInt(dateFilter, 10));
            return orderDate >= pastDate && orderDate <= today;
          })()
        : true;

      const searchTerm = searchID.trim();
      const matchSearchID = searchTerm
        ? (order.idsOrder && order.idsOrder.toString().includes(searchTerm)) ||
          (order.supplier?.id && order.supplier.id.toString().includes(searchTerm)) ||
          (order.supplier?.custom_id && order.supplier.custom_id.toString().includes(searchTerm)) ||
          (order.customer_info?.Name && order.customer_info.Name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (order.customer_info?.Phone && order.customer_info.Phone.includes(searchTerm))
        : true;

      return matchStatus && matchDate && matchSearchID;
    });

    const selectedOrders = orders.filter((order) => selectedOrderIds.includes(getOrderUniqueId(order)));
    const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every((order) => selectedOrderIds.includes(getOrderUniqueId(order)));

    const toggleOrderSelection = (order) => {
      const orderId = getOrderUniqueId(order);
      setSelectedOrderIds((prev) =>
        prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
      );
    };

    const toggleSelectAllFiltered = () => {
      if (!filteredOrders.length) return;
      if (allFilteredSelected) {
        const filteredIds = new Set(filteredOrders.map((order) => getOrderUniqueId(order)));
        setSelectedOrderIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      } else {
        const filteredIds = filteredOrders.map((order) => getOrderUniqueId(order));
        setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
      }
    };

    const ensureSelectionOrAlert = () => {
      if (!selectedOrders.length) {
        alert(t('orders_export.no_selection'));
        return false;
      }
      return true;
    };

    const exportButtonBaseStyle = {
      backgroundColor: '#000000',
      color: 'white',
      borderRadius: '25px',
      padding: '8px 20px',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 2px 6px rgba(19, 59, 140, 0.4)',
      transition: 'background-color 0.3s ease',
    };

    const formatCurrency = (amount) => {
      const numericValue = Number(amount);
      return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)} ${t('currency')}` : `0.00 ${t('currency')}`;
    };

    const resolveLocalizedValue = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value.trim();
      if (Array.isArray(value)) {
        return value.map((item) => resolveLocalizedValue(item)).filter(Boolean).join(' ').trim();
      }
      if (typeof value === 'object') {
        const langVariants = [currentLang, currentLang?.split('-')?.[0], 'fr', 'ar', 'en', 'default'];
        for (const key of langVariants) {
          if (!key) continue;
          const candidate = value[key];
          const resolved = resolveLocalizedValue(candidate);
          if (resolved) return resolved;
        }
        for (const candidate of Object.values(value)) {
          const resolved = resolveLocalizedValue(candidate);
          if (resolved) return resolved;
        }
      }
      return '';
    };

    const resolveProductName = (product) => {
      if (!product || typeof product !== 'object') return '';
      const sourceCandidates = [
        product.Name, product.name, product.Title, product.title,
        product.ProductName, product.productName, product.description?.name, product.translations?.name,
      ];
      for (const candidate of sourceCandidates) {
        const resolved = resolveLocalizedValue(candidate);
        if (resolved) return resolved;
      }
      return '';
    };

    const buildProductSummary = (order) => {
      const summary = order.order_items?.map((item) => {
        const name = resolveProductName(item.product) || t('Produit', 'Produit');
        return `${name} (x${item.Quantity || 0})`;
      }) || [];
      return summary.join('; ');
    };

    const buildProductNames = (order) => {
      const names = order.order_items?.map((item) => resolveProductName(item.product) || t('Produit', 'Produit')) || [];
      return names.filter(Boolean).join(', ');
    };

    const buildTotalQuantity = (order) => {
      return (order.order_items || []).reduce((acc, item) => acc + Number(item.Quantity ?? 0), 0);
    };

    const handleExportSelectedToCsv = () => {
      if (!ensureSelectionOrAlert()) return;

      const headers = ['Order ID', 'Supplier', 'Client', 'Phone', 'Address', 'Status', 'Payment Status', 'Gain', 'Amount', 'Created At', 'Products'];
      const rows = selectedOrders.map((order) => {
        const supplierName = order.supplier?.name || '';
        const customerName = order.customer_info?.Name || '';
        const phone = order.customer_info?.Phone || '';
        const address = order.customer_info?.Address || '';
        const values = [
          order.idsOrder ?? order.OrderID ?? '',
          supplierName, customerName, phone, address,
          order.Status || order.status || '',
          order.payment?.PaymentStatus || '',
          order.gain || '',
          order.payment?.Amount || '',
          order.created_at ? new Date(order.created_at).toLocaleString() : '',
          buildProductSummary(order),
        ];
        return values.map((val) => `"${(val ?? '').toString().replace(/"/g, '""')}"`).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders-export-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const handleExportSelectedToPdf = () => {
      if (!ensureSelectionOrAlert()) return;
      const doc = new jsPDF();

      selectedOrders.forEach((order, index) => {
        if (index > 0) doc.addPage();
        let cursorY = 20;

        doc.setFontSize(16);
        doc.text(`${t('ID_de_commande')}: ${order.idsOrder ?? order.OrderID ?? ''}`, 14, cursorY);
        cursorY += 10;

        doc.setFontSize(12);
        doc.text(`${t('Fournisseur')}: ${order.supplier?.name || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Client')}: ${order.customer_info?.Name || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Numéro_de_Téléphone')}: ${order.customer_info?.Phone || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Adresse')}: ${order.customer_info?.Address || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Created_at')}: ${order.created_at ? new Date(order.created_at).toLocaleString() : ''}`, 14, cursorY);
        cursorY += 10;

        doc.text(`${t('statut')}: ${order.Status || order.status || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Statut_Paiement')}: ${order.payment?.PaymentStatus || ''}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Gain')}: ${formatCurrency(order.gain)}`, 14, cursorY);
        cursorY += 6;
        doc.text(`${t('Montant')}: ${formatCurrency(order.payment?.Amount)}`, 14, cursorY);
        cursorY += 10;

        doc.text(t('Produits'), 14, cursorY);
        cursorY += 8;

        const items = order.order_items || [];
        if (!items.length) {
          doc.text('N/A', 14, cursorY);
        } else {
          items.forEach((item, idx) => {
            const name = item.product?.Name?.[currentLang] || item.product?.Name?.fr || '';
            const priceAmount = Number(item.Price);
            const formattedPrice = Number.isFinite(priceAmount) ? `${priceAmount.toFixed(2)} ${t('currency')}` : `0.00 ${t('currency')}`;
            doc.text(`${idx + 1}. ${name} x${item.Quantity || 0} - ${formattedPrice}`, 14, cursorY);
            cursorY += 6;
          });
        }
      });

      doc.save(`orders-export-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const handleSendToDelivery = async (order) => {
      if (!order) return;

      const orderId = getOrderUniqueId(order);
      const customer = order.customer_info || {};
      const fullname = (customer.Name || '').trim();
      const phone = (customer.Phone || '').trim();
      const city = (customer.City || customer.city || '').trim();
      const address = (customer.Address || '').trim();
      const note = (order.notes || '').trim();
      const priceValue = Number(order.payment?.Amount ?? 0);
      const price = Number.isFinite(priceValue) ? priceValue.toFixed(2) : '0';
      const productsTitle = buildProductNames(order) || 'Commande WMG';
      const totalQuantity = buildTotalQuantity(order) || 1;

      if (!fullname || !phone || !city || !address) {
        alert("Informations client incomplètes pour l'export vers GoodDelivery.");
        return;
      }

      const query = buildGoodDeliveryQuery({
        fullname, phone, city, address, price,
        product: productsTitle, qty: String(totalQuantity), note, change: '0', openpackage: '1',
      });

      const url = `https://gooddelivery.ma/addcolis.php?${query}`;

      try {
        setDeliveryLoadingId(orderId);
        await fetch(url, { method: 'GET', mode: 'no-cors' });
        alert('Commande envoyée à GoodDelivery.');
      } catch (error) {
        const popup = window.open(url, '_blank', 'noopener,noreferrer');
        if (popup) {
          popup.focus();
          alert("La requête a été ouverte dans un nouvel onglet à cause d'une restriction CORS.");
        } else {
          alert("Échec de l'export vers le transporteur.");
        }
      } finally {
        setDeliveryLoadingId((current) => (current === orderId ? null : current));
      }
    };

    // --- Edit Modal Handlers ---
    const handleOpenEditModal = (order) => {
      setEditingOrder(order);
      setEditForm({
        Name: order.customer_info?.Name || '',
        Phone: order.customer_info?.Phone || '',
        Address: order.customer_info?.Address || '',
        City: order.customer_info?.City || order.customer_info?.city || '',
        notes: order.notes || '',
        TotalAmount: order.payment?.Amount || order.total || 0,
        items: (order.order_items || []).map(item => ({
          ProductID: item.ProductID,
          Name: resolveProductName(item.product) || 'Produit',
          Quantity: item.Quantity,
          Price: item.Price
        }))
      });
      setShowEditModal(true);
    };

    const handleQuantityChange = (index, delta) => {
      const updatedItems = [...editForm.items];
      const newQty = updatedItems[index].Quantity + delta;
      if (newQty < 1) return;
      updatedItems[index].Quantity = newQty;

      const newTotal = updatedItems.reduce((acc, curr) => acc + (Number(curr.Price) * Number(curr.Quantity)), 0);
      setEditForm({ ...editForm, items: updatedItems, TotalAmount: newTotal });
    };

    const handleRemoveItem = (index) => {
      const updatedItems = editForm.items.filter((_, i) => i !== index);
      const newTotal = updatedItems.reduce((acc, curr) => acc + (Number(curr.Price) * Number(curr.Quantity)), 0);
      setEditForm({ ...editForm, items: updatedItems, TotalAmount: newTotal });
    };

    const handleSaveEdit = async () => {
      if (!editingOrder) return;
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const payload = {
          Name: editForm.Name,
          Phone: editForm.Phone,
          Address: editForm.Address,
          City: editForm.City,
          notes: editForm.notes,
          TotalAmount: editForm.TotalAmount,
          Status: editingOrder.Status || editingOrder.status,
          items: editForm.items.map(item => ({
            ProductID: item.ProductID,
            Quantity: item.Quantity,
            Price: item.Price
          }))
        };

const response = await axios.put(`https://jolyaparis.cloud/api/admin/orders/${editingOrder.OrderID}`, payload, {
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }
});

        if (response.status === 200) {
          alert('Commande mise à jour avec succès.');
          setShowEditModal(false);
          fetchAllOrders(currentPage);
        } else {
          alert('Échec de la mise à jour.');
        }
      } catch (error) {
        console.error('Erreur mise à jour:', error);
        alert("Une erreur s'est produite lors de la mise à jour.");
      }
    };

    const handleDeleteOrder = async (orderID, status) => {
      if (!status) return;
      
      const normalizedStatus = status.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
      if (normalizedStatus === 'livre' || normalizedStatus === 'annule') return;

      if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette commande ?")) return;

      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const response = await axios.delete(`https://jolyaparis.cloud/api/admin/orders/${orderID}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          alert("Commande supprimée avec succès.");
          fetchAllOrders(currentPage);
        } else {
          alert("Échec de la suppression.");
        }
      } catch (error) {
        alert("Une erreur s'est produite.");
      }
    };

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <NotificationBell />
            
            <div className="d-flex">
                <section className="w-100 p-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-center mb-4 gap-4">
                    <h1 className="text-dark display-5 w-100 text-center mb-3">
                      {t('Tableau_de_bord_commandes')}
                    </h1>

                    <div className="d-flex flex-wrap align-items-center gap-3 justify-content-center">
                      <div className="d-flex flex-column">
                        <label className="form-label fw-bold text-dark mb-1 text-center">{t('Filtrer_par_statut')}</label>
                        <select
                          className="form-select border-dark shadow-sm"
                          style={{ minWidth: '180px', maxWidth: '220px' }}
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">{t('Tous_les_statuts')}</option>
                          <option value="en attente">{t('en_attente')}</option>
                          <option value="attribué">{t('attribué')}</option>
                          <option value="en transit">{t('en_transit')}</option>
                          <option value="livré">{t('livré')}</option>
                          <option value="annulé">{t('annulé')}</option>
                        </select>
                      </div>

                      <div className="d-flex flex-column">
                        <label className="form-label fw-bold text-dark mb-1 text-center">{t('Filtrer_par_date')}</label>
                        <select
                          className="form-select border-dark shadow-sm"
                          style={{ minWidth: '180px', maxWidth: '220px' }}
                          value={dateFilter}
                          onChange={(e) => setDateFilter(e.target.value)}
                        >
                          <option value="">{t('Toutes_les_dates')}</option>
                          <option value="7">{t('7_derniers_jours')}</option>
                          <option value="15">{t('15_derniers_jours')}</option>
                          <option value="30">{t('30_derniers_jours')}</option>
                        </select>
                      </div>

                      <div className="d-flex flex-column">
                        <label className="form-label fw-bold text-dark mb-1 text-center">{t('Recherche_par_ID')}</label>
                        <input
                          type="text"
                          className="form-control border-dark shadow-sm"
                          style={{ minWidth: '180px', maxWidth: '220px' }}
                          placeholder={t('Recherche_par_ID_nom_ou_téléphone')}
                          value={searchID}
                          onChange={(e) => setSearchID(e.target.value)}
                        />
                      </div>

                      <div className="d-flex align-items-end">
                        <button
                          className="btn"
                          onClick={() => { setStatusFilter(''); setDateFilter(''); setSearchID(''); }}
                          style={{ height: '38px', backgroundColor: '#000000', color: 'white', borderRadius: '25px', padding: '0 20px', border: 'none', cursor: 'pointer', marginTop: '30px' }}
                        >
                          {t('Reinitialiser_les_filtres')}
                        </button>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap justify-content-center gap-3 mt-3 w-100">
                      <button
                        type="button"
                        className="btn"
                        style={{ ...exportButtonBaseStyle, opacity: selectedOrderIds.length ? 1 : 0.6, cursor: selectedOrderIds.length ? 'pointer' : 'not-allowed' }}
                        disabled={!selectedOrderIds.length}
                        onClick={handleExportSelectedToCsv}
                      >
                        {t('orders_export.export_csv')}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{ ...exportButtonBaseStyle, opacity: selectedOrderIds.length ? 1 : 0.6, cursor: selectedOrderIds.length ? 'pointer' : 'not-allowed' }}
                        disabled={!selectedOrderIds.length}
                        onClick={handleExportSelectedToPdf}
                      >
                        {t('orders_export.export_pdf')}
                      </button>
                    </div>
                  </div>
                  
                  <hr className="my-4" />

                  {/* Orders Table */}
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-primary">
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input type="checkbox" className="form-check-input" onChange={toggleSelectAllFiltered} checked={allFilteredSelected && filteredOrders.length > 0} />
                          </th>
                          <th>{t('ID_de_commande')}</th>
                          <th>{t('Fournisseur')}</th>
                          <th>{t('Client')}</th>
                          <th>{t('Statut_du_paiement')}</th>
                          <th>{t('Produits')}</th>
                          <th>{t('Montant')}</th>
                          <th>{t('Gain')}</th>
                          <th>{t('Notes')}</th>
                          <th>{t('created_at')}</th>
                          <th>{t('statut')}</th>
                          <th>{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((order) => {
                          const orderId = getOrderUniqueId(order);
                          const isSelected = selectedOrderIds.includes(orderId);
                          const normalizedStatus = (order.Status ?? order.status ?? '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

                          const isDeletable = normalizedStatus !== 'livre' && normalizedStatus !== 'annule' && normalizedStatus !== 'pas_de_reponse' && normalizedStatus !== 'reporte';

                          const supplierName = order.supplier?.name || t('Commandes_sans_code_promo');
                          const supplierCode = order.supplier?.custom_id || '';
                          const customerName = order.customer_info?.Name || '';
                          const customerPhone = order.customer_info?.Phone || '';
                          const customerAddress = order.customer_info?.Address || '';
                          const customerCity = order.customer_info?.City || order.customer_info?.city || '';
                          const paymentStatus = order.payment_type|| '';
                          const orderItems = Array.isArray(order.order_items) ? order.order_items : [];
                          const amountDisplay = formatCurrency(order.payment?.Amount);
                          const gainDisplay = formatCurrency(order.gain);
                          const createdAtDisplay = order.created_at ? new Date(order.created_at).toLocaleString() : '';
                          const orderStatus = order.Status ?? order.status ?? '';

                          return (
                            <tr key={orderId || order.OrderID}>
                              <td>
                                <input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => toggleOrderSelection(order)} />
                              </td>
                              <td>{order.idsOrder ?? order.OrderID ?? ''}</td>
                              <td>
                                {supplierName}<br />
                                <small className="text-muted">{supplierCode}</small>
                              </td>
                              <td>
                                {customerName}&nbsp;<br/>
                                <a href={`tel:${customerPhone}`} style={{color: 'blue', textDecoration: 'underline'}}>{customerPhone}<br/></a>&nbsp;
                                {customerAddress}
                                {customerCity && (<><br /><span className="text-muted">{customerCity}</span></>)}
                              </td>
                              <td>{paymentStatus}</td>
                              <td>
                                {orderItems.map((item) => (
                                  <div key={item.OrderItemID}>
                                    {resolveProductName(item.product) || t('Produit', 'Produit')} <br/>(x{item.Quantity})
                                  </div>
                                ))}
                              </td>
                              <td>{amountDisplay}</td>
                              <td>{gainDisplay}</td>
                              <td>{order.notes || ''}</td>
                              <td>{createdAtDisplay}</td>
                              <td>
                                <select
                                  value={orderStatus}
                                  onChange={(e) => handleStatusChange(order.OrderID, e.target.value, paymentStatus)}
                                  className="form-control"
                                  disabled={orderStatus === 'livré' || orderStatus === 'annulé'}
                                >
                                  <option value="en attente">{t('en_attente')}</option>
                                  <option value="en transit">{t('en_transit')}</option>
                                  <option value="reporte">{t('reporte')}</option>
                                  <option value="pas_de_reponse">{t('pas_de_reponse')}</option>
                                  <option value="livré">{t('livré')}</option>
                                  <option value="annulé">{t('annulé')}</option>
                                </select>
                              </td>
                            <td>
  <div className="d-flex align-items-center gap-1">
    {/* Edit Button */}
    <button
      type="button"
      className="btn btn-sm text-white"
      style={{ backgroundColor: '#0d6efd', minWidth: '32px' }}
      onClick={() => handleOpenEditModal(order)}
      title="Modifier"
    >
      <i className="fas fa-edit"></i>
    </button>

    {/* Send to Delivery Button */}
    <button
      type="button"
      className="btn btn-sm text-white text-nowrap"
      style={{ backgroundColor: '#edbf03' }}
      disabled={deliveryLoadingId === orderId}
      onClick={() => handleSendToDelivery(order)}
    >
      {deliveryLoadingId === orderId ? t('En_cours') : t('orders.send_to_delivery', 'Envoyer')}
    </button>

    {/* Delete Button */}
    <button
      type="button"
      className="btn btn-sm text-white"
      style={{ backgroundColor: 'red', minWidth: '32px' }}
      disabled={!isDeletable}
      onClick={() => handleDeleteOrder(order.OrderID, orderStatus)}
      title="Supprimer"
    >
      <i className="fas fa-trash"></i>
    </button>
  </div>
</td>                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-center">
                    <nav>
                      <ul className="pagination">
                        <li className="page-item" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                          <button className="page-link">{t('Previous')}</button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(index + 1)}>{index + 1}</button>
                          </li>
                        ))}
                        <li className="page-item" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                          <button className="page-link">{t('Next')}</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </section>
            </div>

            {/* --- Edit Order Modal --- */}
            {showEditModal && (
              <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content rounded-4 shadow-lg">
                    <div className="modal-header border-0 pb-0">
                      <h5 className="modal-title fw-bold">
                        Modifier Commande #{editingOrder?.idsOrder ?? editingOrder?.OrderID}
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                    </div>
                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold small">Nom Client</label>
                          <input type="text" className="form-control" value={editForm.Name} onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold small">Téléphone</label>
                          <input type="text" className="form-control" value={editForm.Phone} onChange={(e) => setEditForm({ ...editForm, Phone: e.target.value })} />
                        </div>
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold small">Adresse</label>
                          <input type="text" className="form-control" value={editForm.Address} onChange={(e) => setEditForm({ ...editForm, Address: e.target.value })} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold small">Ville</label>
                          <input type="text" className="form-control" value={editForm.City} onChange={(e) => setEditForm({ ...editForm, City: e.target.value })} />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold small">Produits</label>
                        {editForm.items.map((item, index) => (
                          <div key={index} className="d-flex align-items-center justify-content-between p-3 mb-2 border rounded-3 bg-light">
                            <div className="me-2" style={{ flex: 1 }}>
                              <span className="fw-bold" style={{ fontSize: '14px' }}>{item.Name}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="small text-muted">Qty:</span>
                              <input
                                type="number"
                                className="form-control text-center"
                                style={{ width: '70px' }}
                                value={item.Quantity}
                                min="1"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  const diff = val - item.Quantity;
                                  handleQuantityChange(index, diff);
                                }}
                              />
                              <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveItem(index)}>
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold small">Total de la commande (DH)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control bg-success-subtle fw-bold"
                          value={editForm.TotalAmount}
                          onChange={(e) => setEditForm({ ...editForm, TotalAmount: parseFloat(e.target.value) || 0 })}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold small">Notes</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Ajouter une note..."
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        ></textarea>
                      </div>
                    </div>
                    <div className="modal-footer border-0">
                      <button type="button" className="btn btn-secondary px-4 rounded-pill" onClick={() => setShowEditModal(false)}>
                        Annuler
                      </button>
                      <button type="button" className="btn btn-primary px-4 rounded-pill" style={{ backgroundColor: '#0d6efd' }} onClick={handleSaveEdit}>
                        Enregistrer les modifications
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </AnimationRevealPage>
    );
};

export default OrdersAdmin;